import React, { type ChangeEvent } from 'react'
import { type Control, Controller, useFormState } from 'react-hook-form'
import InputLabel from '@mui/material/InputLabel'
import TextField, { type TextFieldProps } from '@mui/material/TextField'
import Box from '@mui/material/Box'
import { grey } from '../../../colors'
import ErrorIconSvg from '../../../assets/alert-circle.svg'
import HelpiconSvg from '../../../assets/question-icon.svg'

interface FormInputProps {
  name: string
  label?: string
  placeholder?: string
  control?: Control<any>
  textFieldProps?: TextFieldProps
  type?: string
  fullWidth?: boolean
  disabled?: boolean
  required?: boolean
  size?: 'small' | 'medium'
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
  showAdornment?: boolean
  width?: string
  defaultValue?: string
  helperText?: string
  wrapperStyle?: React.CSSProperties
}

const FormInput: React.FC<FormInputProps> = ({
  name,
  label,
  control,
  textFieldProps,
  placeholder,
  type = 'text',
  disabled = false,
  required,
  size = 'medium',
  fullWidth = true,
  onChange,
  showAdornment = false,
  defaultValue,
  width,
  helperText,
  wrapperStyle,
  ...rest
}) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Box minHeight={'72px'} sx={wrapperStyle}>
          {label && (
            <InputLabel required={required} sx={{ marginBottom: 2, color: grey[950] }}>
              {label}
            </InputLabel>
          )}
          <TextField
            sx={{ width }}
            defaultValue={defaultValue}
            error={Boolean(error?.message)}
            {...rest}
            type={type}
            placeholder={placeholder}
            {...field}
            fullWidth={fullWidth}
            disabled={disabled}
            helperText={error?.message as string ?? helperText}
            FormHelperTextProps={{ sx: { whiteSpace: 'pre-line' } }}
            size={size}
            InputProps={{
              inputProps: {
                min: 0
              },
              endAdornment: showAdornment && (
                <>
                  {error != null && <ErrorIcon />}
                  {error == null && <InfoIcon />}
                </>
              )
            }}
            {...textFieldProps}
            onChange={(e) => {
              field.onChange(e)
              if (onChange != null) {
                onChange(e as ChangeEvent<HTMLInputElement>)
              }
            }}
          />
        </Box>
      )}
    />
  )
}

const ErrorIcon = (): JSX.Element => (
  <img src={ErrorIconSvg} onClick={() => {}} />
)

const InfoIcon = (): JSX.Element => (
  <img src={HelpiconSvg} onClick={() => {}} ></img>
)

export default FormInput
